import type { CSSProperties, ReactNode } from "react"
import {
  firmAccentForeground,
  firmAccentSolid,
} from "@ledgerbox/design-system/brand"
import type { PortalBrandingResponse } from "@ledgerbox/domain"

import { ChecklistSkeleton, HeadingSkeleton, LoadingRegion } from "./skeleton"
import { LedgerBoxMark, useNarrowScreen } from "./ui"

export type PortalView = "checklist" | "messages" | "foryou" | "history"

/** What each section is called. The same four names on every portal, in
 * the header and in the phone's tab bar. */
export const sectionLabels = {
  checklist: "To send",
  messages: "Messages",
  foryou: "For you",
  history: "History",
} satisfies Record<PortalView, string>

export function PortalShell({
  branding,
  clientName,
  nav,
  tabs,
  help,
  account,
  toFix,
  signedInAs,
  children,
}: {
  branding: PortalBrandingResponse
  clientName?: string | null
  /** Who is signed in. The header names them; on a phone, where the header
   * has room for the way out only, the client line under it does. */
  signedInAs?: string | null
  nav?: ReactNode
  tabs?: ReactNode
  help?: ReactNode
  account?: ReactNode
  /** "3 items to fix", while the firm has items sent back. */
  toFix?: ReactNode
  children: ReactNode
}) {
  // On a phone "1 item to fix" beside the help and the way out cut the
  // firm's name to "Ledge…", so it takes its own line under the header
  // there (R3-U4).
  const narrow = useNarrowScreen()
  const headerFix = narrow ? null : toFix
  return (
    <div
      className="portal-shell"
      // SAFETY: React's CSSProperties omits application-owned custom properties,
      // while the value is validated as a hex color by the branding schema.
      style={
        {
          "--firm-accent": branding.accentColor,
          // The pill fill is computed, not a fixed CSS mix: a mid-tone accent
          // is darkened until its text reads at 4.5:1 (design-system brand.ts).
          "--accent-solid": firmAccentSolid(branding.accentColor),
          "--firm-accent-foreground": firmAccentForeground(
            branding.accentColor
          ),
        } as CSSProperties
      }
    >
      <header className="portal-header">
        <div className="brand-lockup">
          {branding.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt={`${branding.displayName} logo`}
              className="firm-logo"
            />
          ) : (
            <span className="firm-monogram" aria-hidden="true">
              {branding.displayName.charAt(0).toUpperCase()}
            </span>
          )}
          {/* A logo that already spells the firm's name can stand alone. */}
          {branding.logoUrl && branding.showNameWithLogo === false ? (
            <span className="visually-hidden">{branding.displayName}</span>
          ) : (
            <span>{branding.displayName}</span>
          )}
        </div>
        {nav}
        {/* What to fix, help and the way out stay in the header on a
            phone, where the sections move to the tab bar. */}
        {headerFix || help || account ? (
          <div className="header-end">
            {headerFix}
            {help}
            {account}
          </div>
        ) : null}
      </header>
      {clientName ? (
        <div className="portal-client-context">
          <span>
            Client: <strong>{clientName}</strong>
          </span>
          {signedInAs ? (
            <span className="client-context-account">
              Signed in as <strong>{signedInAs}</strong>
            </span>
          ) : null}
        </div>
      ) : null}
      {narrow && toFix ? (
        <div className="portal-client-context">{toFix}</div>
      ) : null}
      <main className="portal-main">{children}</main>
      <footer className="portal-footer">
        {branding.supportEmail || branding.supportPhone ? (
          <div className="portal-support">
            <span>
              Need help? Contact {branding.supportName || branding.displayName}.
            </span>
            {branding.supportEmail ? (
              <a href={`mailto:${branding.supportEmail}`}>
                {branding.supportEmail}
              </a>
            ) : null}
            {branding.supportPhone ? (
              <a href={`tel:${branding.supportPhone}`}>
                {branding.supportPhone}
              </a>
            ) : null}
          </div>
        ) : null}
        <div className="portal-powered-by">
          <span>Secured by</span>
          <a
            href="https://ledgerbox.io"
            target="_blank"
            rel="noreferrer"
            aria-label="LedgerBox"
          >
            <img
              className="ledgerbox-wordmark"
              src="/brand/logo-ledgerbox.svg?v=lime-box"
              alt="LedgerBox"
              width={112}
              height={11}
            />
          </a>
        </div>
      </footer>
      {tabs}
    </div>
  )
}

/** Who the portal thinks is here, and the way out for anyone it is wrong
 * about: a forwarded email signs in as the person it was sent to. On a phone
 * the header has room for the button only, so it says "Sign out". */
export function AccountMenu({
  name,
  onSignOut,
}: {
  name: string
  onSignOut(): void
}) {
  return (
    <div className="account-menu">
      <span>
        Signed in as <strong>{name}</strong>
      </span>
      <button className="text-button" type="button" onClick={onSignOut}>
        <span className="account-question">Not you? </span>Sign out
      </button>
    </div>
  )
}

/** The header's four sections. On a phone the header keeps the firm's name
 * only and the tab bar at the foot takes over. */
/** A section's count of things waiting on the client, read as words by a
 * screen reader. Zero shows nothing. */
function NavCount({ value }: { value: number | undefined }) {
  if (!value) return null
  return (
    <span className="nav-count">
      <span aria-hidden="true">{value}</span>
      <span className="visually-hidden">, {value} new</span>
    </span>
  )
}

export function PortalNav({
  view,
  counts,
  onChange,
}: {
  view: PortalView
  /** What waits on the client in each section. */
  counts?: Partial<Record<PortalView, number>>
  onChange(next: PortalView): void
}) {
  const sections: PortalView[] = ["checklist", "messages", "foryou", "history"]
  return (
    <nav className="header-nav" aria-label="Client portal sections">
      {sections.map((value) => (
        <button
          key={value}
          type="button"
          className={view === value ? "is-active" : ""}
          aria-current={view === value ? "page" : undefined}
          onClick={() => onChange(value)}
        >
          {sectionLabels[value]}
          <NavCount value={counts?.[value]} />
        </button>
      ))}
    </nav>
  )
}

/* The header's four sections, as the phone's tab bar: the same names in the
   same order, so a client who turns the phone finds the same portal. */
export function PortalTabs({
  view,
  counts,
  onChange,
}: {
  view: PortalView
  counts?: Partial<Record<PortalView, number>>
  onChange(next: PortalView): void
}) {
  const tabs: Array<[PortalView, ReactNode]> = [
    [
      "checklist",
      <path
        key="list"
        d="M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
      />,
    ],
    [
      "messages",
      <path
        key="messages"
        d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
      />,
    ],
    ["foryou", <path key="foryou" d="M20 6L9 17l-5-5" />],
    [
      "history",
      <path key="history" d="M12 7v5l3 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />,
    ],
  ]
  return (
    <nav className="portal-tabs" aria-label="Client portal sections">
      {tabs.map(([value, glyph]) => {
        const label = sectionLabels[value]
        const active = view === value
        return (
          <button
            key={value}
            type="button"
            className={active ? "is-active" : ""}
            aria-current={active ? "page" : undefined}
            onClick={() => onChange(value)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              {glyph}
            </svg>
            <span>
              {label}
              <NavCount value={counts?.[value]} />
            </span>
          </button>
        )
      })}
    </nav>
  )
}

/* The shell and the documents page drawn in paper before the firm's name
   and the client's list arrive, so the page does not jump when they land. */
export function LoadingScreen() {
  return (
    <div className="portal-shell">
      <header className="portal-header" aria-hidden="true">
        <div className="brand-lockup">
          <span className="skeleton sk-mark" />
          <span className="skeleton sk-name" />
        </div>
      </header>
      <main className="portal-main">
        <LoadingRegion label="Opening your portal" className="page sk-page">
          <HeadingSkeleton />
          <ChecklistSkeleton />
        </LoadingRegion>
      </main>
    </div>
  )
}

export function UnknownPortal() {
  return (
    <main className="plain-state">
      <LedgerBoxMark />
      <h1>This link does not open a portal</h1>
      <p>Check the web address, or ask your firm to send the link again.</p>
    </main>
  )
}

export function MissingLink() {
  return (
    <section className="auth-panel">
      <h1>Open the link your firm sent you</h1>
      <p className="auth-copy">
        This web address does not name a client. Use the whole link from your
        firm's email.
      </p>
    </section>
  )
}
